import { FunctionComponent } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import { route, RoutableProps } from 'preact-router';
import { Layout } from '../components/layout';
import { CopyableValue } from '../components/copyable-value';
import { toast } from '../components/toast';
import { generateTkw, TkwResultDay } from '../services/tkw.service';

const VERSION_KEY = 'tkw-mobile.albis-version';
const DAY_NAMES = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

export const TkwPage: FunctionComponent<RoutableProps> = () => {
  const [version, setVersion] = useState('');
  const [date, setDate] = useState(today());
  const [results, setResults] = useState<TkwResultDay[] | null>(null);
  const [showWeek, setShowWeek] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(VERSION_KEY);
    if (saved) setVersion(saved);
  }, []);

  const submit = async (e: Event) => {
    e.preventDefault();
    if (busy) return;

    setError(null);
    setBusy(true);
    setResults(null);
    setShowWeek(false);

    try {
      const response = await generateTkw(version.trim(), date);
      setResults(response.results || []);
      localStorage.setItem(VERSION_KEY, version.trim());
    } catch (err) {
      const apiError = err as {status?: number; body?: {message?: string}};
      if (apiError.status === 401 || apiError.status === 403) {
        toast('Sitzung abgelaufen', 'error');
        route('/logout', true);
        return;
      }
      setError(apiError.body?.message || 'Generierung fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const copyText = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`Kopiert: ${text}`);
    } catch {
      toast('Konnte nicht kopieren', 'error');
    }
  };

  return (
    <Layout title="TKW Mobile" subtitle="Tageskennwort-Generator" showLogout>
      <form onSubmit={submit}>
        <div class="input-group">
          <label for="version">ALBIS Version</label>
          <input
            id="version"
            type="text"
            value={version}
            onInput={(e) => setVersion((e.target as HTMLInputElement).value)}
            placeholder="z.B. 25.32"
            autocomplete="off"
            required
            disabled={busy}
          />
          <div class="hint">Versionsnummer wie in ALBIS angezeigt</div>
        </div>

        <div class="input-group">
          <label for="date">Datum</label>
          <input
            id="date"
            type="date"
            value={date}
            onInput={(e) => setDate((e.target as HTMLInputElement).value)}
            required
            disabled={busy}
          />
        </div>

        {error && <div class="error">{error}</div>}

        <button type="submit" class="btn" disabled={busy}>
          {busy ? <span class="spinner" /> : 'Generieren'}
        </button>
      </form>

      {results && results.length > 0 && (
        <div class="mt-md">
          <CopyableValue label={`Tageskennwort (${formatDay(results[0])})`} value={results[0].tkw} />
          <CopyableValue label="Adminkennwort" value={results[0].admin} />

          {!showWeek && (
            <button class="btn secondary mt-md" onClick={() => setShowWeek(true)}>
              7 Tage anzeigen
            </button>
          )}

          {showWeek && (
            <table class="week-table">
              <thead>
                <tr><th>Tag</th><th>TKW</th><th>Admin</th></tr>
              </thead>
              <tbody>
                {results.map((day) => (
                  <tr class={day.date === today() ? 'today' : ''}>
                    <td>{formatDay(day)}</td>
                    <td class="code" onClick={() => copyText(day.tkw)}>{day.tkw}</td>
                    <td class="code" onClick={() => copyText(day.admin)}>{day.admin}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

    </Layout>
  );
};

function today(): string {
  return new Date().toISOString().split('T')[0];
}

function formatDay(day: TkwResultDay): string {
  const match = day.date.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return day.date;
  }
  const year = match[1];
  const month = match[2];
  const dayNum = match[3];
  const dow = DAY_NAMES[new Date(`${day.date}T00:00:00`).getDay()] || '';
  return `${dow ? dow + ' ' : ''}${dayNum}.${month}.${year}`;
}
